import { create } from "zustand";

export interface AudioTrack {
  id: string;
  src: string;
  title: string;
}

interface AudioPlayerState {
  activeTrack: AudioTrack | null;
  isVisible: boolean;
  isAudioReady: boolean;
  duration: number;
  currentTime: number;
  trackDurations: Record<string, number>;
  trackCurrentTimes: Record<string, number>;
  trackPlaybackRates: Record<string, number>;
  isPlaying: boolean;
  playbackRate: number;
  volume: number;
  togglePlay: (track: AudioTrack) => Promise<void>;
  seekTrackByPercent: (track: AudioTrack, percent: number) => void;
  seekByPercent: (percent: number) => void;
  skip: (seconds: number) => void;
  setPlaybackRate: (rate: number) => void;
  setTrackPlaybackRate: (track: AudioTrack, rate: number) => void;
  setVolume: (volume: number) => void;
  stop: () => void;
}

const hasAudioSupport = typeof window !== "undefined" && typeof Audio !== "undefined";
const audioElement = hasAudioSupport ? new Audio() : null;

if (audioElement) {
  audioElement.preload = "metadata";
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
const getTrackStorageKey = (track: AudioTrack) => `${track.id}::${track.src}`;

export const useAudioPlayerStore = create<AudioPlayerState>((set, get) => {
  if (audioElement) {
    audioElement.addEventListener("loadedmetadata", () => {
      const duration = Number.isFinite(audioElement.duration) ? audioElement.duration : 0;
      const activeTrack = get().activeTrack;

      set((state) => {
        if (!activeTrack) {
          return {
            duration,
            isAudioReady: true,
          };
        }

        const trackKey = getTrackStorageKey(activeTrack);
        return {
          duration,
          isAudioReady: true,
          trackDurations: {
            ...state.trackDurations,
            [trackKey]: duration,
          },
        };
      });
    });

    audioElement.addEventListener("timeupdate", () => {
      const nextTime = audioElement.currentTime;
      const activeTrack = get().activeTrack;

      set((state) => {
        if (!activeTrack) {
          return { currentTime: nextTime };
        }

        const trackKey = getTrackStorageKey(activeTrack);
        return {
          currentTime: nextTime,
          trackCurrentTimes: {
            ...state.trackCurrentTimes,
            [trackKey]: nextTime,
          },
        };
      });
    });

    audioElement.addEventListener("play", () => {
      set({ isPlaying: true, isVisible: true });
    });

    audioElement.addEventListener("pause", () => {
      set({ isPlaying: false });
    });

    audioElement.addEventListener("ended", () => {
      const activeTrack = get().activeTrack;

      set((state) => {
        if (!activeTrack) {
          return { isPlaying: false, currentTime: 0 };
        }

        const trackKey = getTrackStorageKey(activeTrack);
        return {
          isPlaying: false,
          currentTime: 0,
          trackCurrentTimes: {
            ...state.trackCurrentTimes,
            [trackKey]: 0,
          },
        };
      });
    });
  }

  return {
    activeTrack: null,
    isVisible: false,
    isAudioReady: false,
    duration: 0,
    currentTime: 0,
    trackDurations: {},
    trackCurrentTimes: {},
    trackPlaybackRates: {},
    isPlaying: false,
    playbackRate: 1,
    volume: 1,

    togglePlay: async (track) => {
      if (!audioElement || !track.src) return;

      const { activeTrack, playbackRate, volume, trackPlaybackRates } = get();
      const shouldSwitchTrack =
        !activeTrack || activeTrack.id !== track.id || activeTrack.src !== track.src;

      if (shouldSwitchTrack) {
        const trackKey = getTrackStorageKey(track);
        const nextPlaybackRate = trackPlaybackRates[trackKey] ?? playbackRate;
        audioElement.pause();
        audioElement.src = track.src;
        audioElement.load();
        audioElement.playbackRate = nextPlaybackRate;
        audioElement.volume = volume;

        set({
          activeTrack: track,
          isVisible: true,
          isAudioReady: false,
          duration: 0,
          currentTime: 0,
          isPlaying: false,
          playbackRate: nextPlaybackRate,
        });

        try {
          await audioElement.play();
        } catch (error) {
          console.error("Failed to play audio:", error);
        }
        return;
      }

      if (audioElement.paused) {
        audioElement.playbackRate = playbackRate;
        audioElement.volume = volume;
        try {
          await audioElement.play();
        } catch (error) {
          console.error("Failed to resume audio:", error);
        }
      } else {
        audioElement.pause();
      }
    },

    seekTrackByPercent: (track, percent) => {
      if (!audioElement || !track.src) return;

      const { activeTrack, playbackRate, volume, trackPlaybackRates } = get();
      const nextPercent = clamp(percent, 0, 1);
      const shouldSwitchTrack =
        !activeTrack || activeTrack.id !== track.id || activeTrack.src !== track.src;

      const seekWithKnownDuration = () => {
        const duration = Number.isFinite(audioElement.duration) ? audioElement.duration : 0;
        if (!duration) return;

        const nextTime = nextPercent * duration;
        audioElement.currentTime = nextTime;
        const trackKey = getTrackStorageKey(track);
        set((state) => ({
          currentTime: nextTime,
          duration,
          trackDurations: {
            ...state.trackDurations,
            [trackKey]: duration,
          },
          trackCurrentTimes: {
            ...state.trackCurrentTimes,
            [trackKey]: nextTime,
          },
        }));
      };

      if (shouldSwitchTrack) {
        const trackKey = getTrackStorageKey(track);
        const nextPlaybackRate = trackPlaybackRates[trackKey] ?? playbackRate;
        audioElement.pause();
        audioElement.src = track.src;
        audioElement.load();
        audioElement.playbackRate = nextPlaybackRate;
        audioElement.volume = volume;

        set({
          activeTrack: track,
          isVisible: true,
          isAudioReady: false,
          duration: 0,
          currentTime: 0,
          isPlaying: false,
          playbackRate: nextPlaybackRate,
        });

        if (audioElement.readyState >= 1) {
          seekWithKnownDuration();
          return;
        }

        audioElement.addEventListener("loadedmetadata", seekWithKnownDuration, { once: true });
        return;
      }

      seekWithKnownDuration();
    },

    seekByPercent: (percent) => {
      if (!audioElement) return;
      const { duration, activeTrack } = get();
      if (!duration) return;

      const nextTime = clamp(percent, 0, 1) * duration;
      audioElement.currentTime = nextTime;
      set((state) => {
        if (!activeTrack) {
          return { currentTime: nextTime };
        }

        const trackKey = getTrackStorageKey(activeTrack);
        return {
          currentTime: nextTime,
          trackCurrentTimes: {
            ...state.trackCurrentTimes,
            [trackKey]: nextTime,
          },
        };
      });
    },

    skip: (seconds) => {
      if (!audioElement) return;
      const { duration, activeTrack } = get();
      if (!duration) return;

      const nextTime = clamp(audioElement.currentTime + seconds, 0, duration);
      audioElement.currentTime = nextTime;
      set((state) => {
        if (!activeTrack) {
          return { currentTime: nextTime };
        }

        const trackKey = getTrackStorageKey(activeTrack);
        return {
          currentTime: nextTime,
          trackCurrentTimes: {
            ...state.trackCurrentTimes,
            [trackKey]: nextTime,
          },
        };
      });
    },

    setPlaybackRate: (rate) => {
      if (!audioElement) return;

      audioElement.playbackRate = rate;
      const activeTrack = get().activeTrack;

      set((state) => {
        if (!activeTrack) {
          return { playbackRate: rate };
        }

        const trackKey = getTrackStorageKey(activeTrack);
        return {
          playbackRate: rate,
          trackPlaybackRates: {
            ...state.trackPlaybackRates,
            [trackKey]: rate,
          },
        };
      });
    },

    setTrackPlaybackRate: (track, rate) => {
      if (!track.src) return;
      const safeRate = clamp(rate, 0.5, 2);
      const activeTrack = get().activeTrack;
      const isActiveTrack = Boolean(
        activeTrack && activeTrack.id === track.id && activeTrack.src === track.src,
      );

      if (isActiveTrack) {
        get().setPlaybackRate(safeRate);
        return;
      }

      const trackKey = getTrackStorageKey(track);
      set((state) => ({
        trackPlaybackRates: {
          ...state.trackPlaybackRates,
          [trackKey]: safeRate,
        },
      }));
    },

    setVolume: (volume) => {
      if (!audioElement) return;

      const safeVolume = clamp(volume, 0, 1);
      audioElement.volume = safeVolume;
      set({ volume: safeVolume });
    },

    stop: () => {
      if (!audioElement) return;

      audioElement.pause();
      audioElement.currentTime = 0;
      set({
        activeTrack: null,
        isVisible: false,
        isPlaying: false,
        isAudioReady: false,
        duration: 0,
        currentTime: 0,
      });
    },
  };
});
